import { ITEM_CARD_SEQUENCE_LIST_VIEW, SPREADSHEET_VIEW, TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceUserActions from "../../constants/interfaceUserActions";
import screenModes from "../../constants/screenModes";
import { appStateActionTypes, appStateSelectors } from "../../modules/appState";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { itemActionTypes } from "../../modules/items";
// import { sequenceActionTypes } from "../../modules/sequence";
import preprocessorQuery from "../../utils/preprocessorQuery";
import windowDimensions from "../../utils/windowDimensions";
import {
  getEnterEditModeInstruction,
  getFlashPlaceholder,
  getIconPlaceholder,
  getSplitViewInstruction,
  getSwitchViewInstruction,
  getTipIdGenerator,
  Placeholders
} from "../utils";
import getSharedTip, { SharedTipIdentifiers } from "./sharedTips";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

// Group ID
const GROUP_ID = "SPREADSHEET_VIEW";
const GROUP_LABEL = "Working in the Spreadsheet";
const GROUP_DESCRIPTION = `Spreadsheet View allows you to create and edit items without dates.`;
const GROUP_GATE = null;
const GROUP_GATE_MESSAGE = null;
const GROUP_APPLICABLE_GATE = null;
const GROUP_APPLICABLE_GATE_MESSAGE = null;
const GROUP_COMPLETED_MESSAGE =
  "Great work! Now you know how to use Spreadsheet View, you may want to explore some of our other new views.";
const GROUP_LINKS = [
  { label: "Learn about our other views", path: "https://help.timeline.app/article/141-user-interface-overview" },
  {
    label: "Learn more about Spreadsheet View",
    path: "https://help.timeline.app/article/161-spreadsheet-view"
  },
  {
    label: "Chronological vs Narrative Order",
    path: "https://help.timeline.app/article/149-chronological-vs-narrative-order"
  }
];

// Create an ID generator
// this will make IDs of the form: "GROUP_ID / TIP_ID"
const idGenerator = getTipIdGenerator(GROUP_ID);

let tips = [];
if (isDesktop) {
  tips = [
    {
      id: idGenerator("ADD_EVENTS_AT_BOTTOM"),
      label: `Add ${Placeholders.EVENT_PLURAL_LABEL} via Spreadsheet View`,
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        { label: 'Click in the "Type label to create new item" cell.', isNumbered: true },
        { label: `Type a label for your first ${Placeholders.EVENT_LABEL} and press enter.`, isNumbered: true },
        { label: `Repeat to add more ${Placeholders.EVENT_PLURAL_LABEL} to your project.`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === SPREADSHEET_VIEW &&
        action.type === itemActionTypes.CREATE_ITEM &&
        typeSelectors.getEventTypeIds(newState).includes(action.itemType)
    },
    getSharedTip(SharedTipIdentifiers.ORGANIZE_EVENTS, GROUP_ID),
    {
      id: idGenerator("INSERT_EVENTS"),
      label: `Insert ${Placeholders.EVENT_PLURAL_LABEL} below current selection`,
      steps: [
        {
          label: `In Spreadsheet View, select the item immediately above where you want to insert your new item`,
          isNumbered: true
        },
        {
          label: `Click the Sidebar + icon, and select ${Placeholders.EVENT_LABEL} from the popover`,
          isNumbered: true
        },
        {
          label: `A new row will be inserted below your current selection. Type a label for the new item and hit enter`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("ASSIGN_DATES"),
      label: `Drag ${Placeholders.EVENT_PLURAL_LABEL} into Timeline View to give them dates`,
      steps: [
        getSplitViewInstruction(SPREADSHEET_VIEW, TIMELINE_VIEW),
        { label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} you want in your timeline`, isNumbered: true },
        {
          label: `Use the ${getFlashPlaceholder(
            interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
            "drag handle"
          )} ${getIconPlaceholder("dragHandle")} next to a selected ${
            Placeholders.EVENT_LABEL
          } to drag it into the Timeline View.`,
          isNumbered: true
        },
        { label: `You can also assign dates directly in Spreadsheet View or the Inspector`, isNumbered: false }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("CHANGE_COLUMNS"),
      label: "Show relationships in Spreadsheet View",
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        { label: "Click the right-facing arrow in the table header's leftmost column.", isNumbered: true },
        { label: "Toggle on a relationship type to show it as a column.", isNumbered: true }
      ],
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) => {
        if (appStateSelectors.getActiveViewType(newState) === SPREADSHEET_VIEW) {
          const activeViewId = appStateSelectors.getActiveViewId(newState);
          const columns = appStateSelectors.getSpreadsheetOptionsForView(newState, activeViewId).columns;
          const referenceAllIds = referenceSelectors.getReferenceAllIds(newState);
          return columns.find(col => referenceAllIds.includes(col.id));
        }
        return false;
      }
    }
  ];
} else if (isTablet) {
  tips = [
    {
      id: idGenerator("ADD_EVENTS_AT_BOTTOM"),
      label: `Add ${Placeholders.EVENT_PLURAL_LABEL} via Spreadsheet View`,
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        { label: 'Double tap in the "Type label to create new item" cell.', isNumbered: true },
        { label: `Type a label for your first ${Placeholders.EVENT_LABEL} and press enter.`, isNumbered: true },
        { label: `Repeat to add more ${Placeholders.EVENT_PLURAL_LABEL} to your project.`, isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) =>
        appStateSelectors.getActiveViewType(newState) === SPREADSHEET_VIEW &&
        action.type === itemActionTypes.CREATE_ITEM &&
        typeSelectors.getEventTypeIds(newState).includes(action.itemType)
    },
    getSharedTip(SharedTipIdentifiers.ORGANIZE_EVENTS, GROUP_ID),
    {
      id: idGenerator("INSERT_EVENTS"),
      label: `Insert ${Placeholders.EVENT_PLURAL_LABEL} below current selection`,
      steps: [
        getEnterEditModeInstruction(SPREADSHEET_VIEW),
        {
          label: `Tap the ${getFlashPlaceholder(
            interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
            "drag handle"
          )} ${getIconPlaceholder(
            "dragHandle"
          )} to select the item immediately above where you want to insert your new item`,
          isNumbered: true
        },
        { label: `Tap the Sidebar + icon, and select ${Placeholders.EVENT_LABEL} from the popover`, isNumbered: true },
        {
          label: `Tap in the label cell for the newly inserted row and type a label for the new item`,
          isNumbered: true
        }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("ASSIGN_DATES"),
      label: `Drag ${Placeholders.EVENT_PLURAL_LABEL} into Timeline View to give them dates`,
      steps: [
        getSplitViewInstruction(SPREADSHEET_VIEW, TIMELINE_VIEW),
        { label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} you want in your timeline`, isNumbered: true },
        getEnterEditModeInstruction(SPREADSHEET_VIEW),
        {
          label: `Use the ${getFlashPlaceholder(
            interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
            "drag handle"
          )} ${getIconPlaceholder("dragHandle")} next to a selected ${
            Placeholders.EVENT_LABEL
          } to drag it into the Timeline View.`,
          isNumbered: true
        },
        { label: `You can also assign dates directly in Spreadsheet View or the Inspector`, isNumbered: false }
      ],
      accessGate: null,
      gateMessage: null,
      requiresDismissal: true
    },
    {
      id: idGenerator("CHANGE_COLUMNS"),
      label: "Show relationships in Spreadsheet View",
      steps: [
        getSwitchViewInstruction(SPREADSHEET_VIEW),
        { label: "Tap the right-facing arrow in the table header's leftmost column.", isNumbered: true },
        { label: "Toggle on a relationship type to show it as a column.", isNumbered: true }
      ],
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) => {
        if (appStateSelectors.getActiveViewType(newState) === SPREADSHEET_VIEW) {
          const activeViewId = appStateSelectors.getActiveViewId(newState);
          const columns = appStateSelectors.getSpreadsheetOptionsForView(newState, activeViewId).columns;
          const referenceAllIds = referenceSelectors.getReferenceAllIds(newState);
          return columns.find(col => referenceAllIds.includes(col.id));
        }
        return false;
      }
    }
  ];
} else if (isMobile) {
  tips = [
    {
      id: idGenerator("CHANGE_VIEW_TYPE"),
      label: "Change view type",
      steps: [
        {
          label: "Tap on the toolbar view selector to switch between your timeline of events and lists of entities.",
          isNumbered: true
        },
        {
          label:
            "When viewing your timeline or narrative, rotate your device to switch between list and timeline/narrative views.",
          isNumbered: true
        }
      ],
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.SWITCH_MOBILE_VIEW
    },
    {
      id: idGenerator("ADD_EVENT_TIMELINE"),
      label: "Add an event in Timeline View",
      steps: [
        { label: "Rotate your device to landscape mode.", isNumbered: true },
        { label: "Make the focused view Timeline View.", isNumbered: true },
        { label: "Tap on the + icon and choose EVENT_LABEL.", isNumbered: true }
      ],
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) => {
        const dimensions = windowDimensions();
        return (
          dimensions.screenMode === screenModes.MobileLandscape &&
          appStateSelectors.getActiveViewType(newState) === TIMELINE_VIEW &&
          action.type === itemActionTypes.CREATE_ITEM &&
          typeSelectors.getEventTypeIds(newState).includes(action.itemType)
        );
      }
    },
    {
      id: idGenerator("SET_EVENT_DATE_TIMELINE"),
      label: "Set an event’s date in Timeline View",
      steps: [
        { label: "Rotate your device to landscape mode.", isNumbered: true },
        { label: "Make the focused view Timeline View.", isNumbered: true },
        { label: "Long press on the screen to enter Edit Mode.", isNumbered: true },
        { label: "Drag an event to a different date in Timeline View to change the event’s date.", isNumbered: true }
      ],
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.DRAG_IN_TIMELINE
    },
    {
      id: idGenerator("ADD_EVENT_SPREADSHEET"),
      label: "Add an event in Chronological List",
      steps: [
        { label: "Rotate your device to portrait mode.", isNumbered: true },
        { label: "Make the focused view Chronological List.", isNumbered: true },
        { label: "Tap on the + icon and choose EVENT_LABEL.", isNumbered: true }
      ],
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: (action, newState) => {
        const dimensions = windowDimensions();
        return (
          dimensions.screenMode === screenModes.MobilePortrait &&
          appStateSelectors.getMobilePortraitViewType(newState) === ITEM_CARD_SEQUENCE_LIST_VIEW &&
          action.type === itemActionTypes.CREATE_ITEM &&
          typeSelectors.getEventTypeIds(newState).includes(action.itemType)
        );
      }
    },
    {
      id: idGenerator("SET_EVENT_DATE_INSPECTOR"),
      label: "Set an event's date in the Inspector",
      steps: [
        { label: "Rotate your device to portrait mode.", isNumbered: true },
        { label: "Make the focused view Chronological List.", isNumbered: true },
        { label: "Tap on an event to select it.", isNumbered: true },
        { label: "In the inspector, tap on the Dates option.", isNumbered: true },
        {
          label: "Change the event's Start Date by either typing in the field or using the calendar picker",
          isNumbered: true
        }
      ],
      requiresExitEditMode: true,
      accessGate: null,
      gateMessage: null,
      resolve: action =>
        action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
        action.id === interfaceUserActions.SET_START_DATE_INSPECTOR
    }
  ];
}

export default {
  id: GROUP_ID,
  label: GROUP_LABEL,
  description: GROUP_DESCRIPTION,
  accessGate: GROUP_GATE,
  gateMessage: GROUP_GATE_MESSAGE,
  applicableGate: GROUP_APPLICABLE_GATE,
  gateApplicableMessage: GROUP_APPLICABLE_GATE_MESSAGE,
  completedMessage: GROUP_COMPLETED_MESSAGE,
  links: GROUP_LINKS,
  tips
};
